<script setup>
import { reactive, ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'

import NextInput from '@/components/Form/NextInput.vue'
import Button from '@/components/Button.vue'
import Select from '@/components/Form/Select.vue'
import { useAlert } from '@/composables/useToast'
import { AVAILABLE_PERMISSIONS } from '@/constants/permissions'
import { getAgentAvailabilityChanges } from '@/api/agents'

const props = defineProps({
  operator: { type: Object, required: true },
})

const emit = defineEmits(['close'])

const store = useStore()
const { t } = useI18n()

const form = reactive({
  name: props.operator.name,
  role: props.operator.role,
  permissions: [...(props.operator.permissions || [])],
})

const rules = computed(() => ({
  name: { required },
  role: { required },
}))

// The journal answers "why did my status change", so it lives next to the
// rights an administrator already edits here
const HISTORY_PERIODS = [7, 30]
const historyDays = ref(7)
const history = ref([])
const historyLoading = ref(false)
const historyTime = new Intl.DateTimeFormat('ru-RU', {
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

const loadHistory = async () => {
  const accountId = store.getters['auth/getCurrentUser']?.accounts?.[0]?.id
  if (!accountId) return
  historyLoading.value = true
  try {
    const { data } = await getAgentAvailabilityChanges(
      accountId,
      props.operator.id,
      historyDays.value
    )
    history.value = data
  } catch (error) {
    useAlert(t('AGENT_MGMT.STATUS_HISTORY.ERROR'))
  } finally {
    historyLoading.value = false
  }
}

const reasonText = change =>
  t(`AGENT_MGMT.STATUS_HISTORY.REASON.${change.reason}`, {
    name: change.changed_by || '',
  })

onMounted(loadHistory)
watch(historyDays, loadHistory)

const v$ = useVuelidate(rules, form)
const roleOptions = computed(() => [
  { value: 'administrator', label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') },
  { value: 'agent', label: t('AGENT_MGMT.AGENT_TYPES.AGENT') },
])


const uiFlags = computed(() => store.getters['agents/getUIFlags'])

// Password reset: the operator gets an email with a set-password link
const sendingLink = ref(false)
const sendPasswordLink = async () => {
  sendingLink.value = true
  try {
    await store.dispatch('agents/sendPasswordLink', props.operator.id)
    useAlert(t('AGENT_MGMT.PASSWORD_LINK.SUCCESS'))
  } catch (error) {
    useAlert(t('AGENT_MGMT.PASSWORD_LINK.ERROR'))
  } finally {
    sendingLink.value = false
  }
}

const submit = async () => {
  v$.value.$touch()
  if (v$.value.$invalid) return
  try {
    await store.dispatch('agents/update', { id: props.operator.id, ...form })
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS'))
    emit('close')
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'))
  }
}
</script>

<template>
  <form class="flex flex-col gap-5" @submit.prevent="submit">
    <NextInput
      v-model="form.name"
      name="operator_name"
      type="text"
      required
      :label="$t('AGENT_MGMT.ADD.FORM.NAME.LABEL')"
      :placeholder="$t('AGENT_MGMT.ADD.FORM.NAME.PLACEHOLDER')"
      :has-error="v$.name.$error"
      @input="v$.name.$touch"
    />

    <div class="space-y-1">
      <label for="operator_role" class="mb-0.5 text-heading-3 text-n-slate-12">
        {{ $t('AGENT_MGMT.ADD.FORM.ROLE.LABEL') }}
      </label>
      <Select id="operator_role" v-model="form.role" full-width :options="roleOptions" />
    </div>

    <div v-if="form.role === 'agent'" class="space-y-1">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('AGENT_MGMT.PERMISSIONS.LABEL') }}
      </span>
      <p class="text-xs text-n-slate-11">
        {{ $t('AGENT_MGMT.PERMISSIONS.HINT') }}
      </p>
      <label
        v-for="permission in AVAILABLE_PERMISSIONS"
        :key="permission"
        class="flex items-center gap-2 py-1 text-sm text-n-slate-12 cursor-pointer"
      >
        <input
          v-model="form.permissions"
          type="checkbox"
          :value="permission"
          class="w-4 h-4 accent-n-brand"
        />
        {{ $t(`AGENT_MGMT.PERMISSIONS.${permission}`) }}
      </label>
    </div>

    <div class="space-y-1">
      <div class="flex items-center justify-between">
        <span class="text-heading-3 text-n-slate-12">
          {{ $t('AGENT_MGMT.STATUS_HISTORY.LABEL') }}
        </span>
        <div class="flex gap-1">
          <button
            v-for="days in HISTORY_PERIODS"
            :key="days"
            type="button"
            class="px-2 py-0.5 text-xs rounded-lg"
            :class="
              historyDays === days
                ? 'bg-n-alpha-2 text-n-slate-12 font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="historyDays = days"
          >
            {{ $t('AGENT_MGMT.STATUS_HISTORY.DAYS', { days }) }}
          </button>
        </div>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ $t('AGENT_MGMT.STATUS_HISTORY.HINT') }}
      </p>
      <p
        v-if="!historyLoading && !history.length"
        class="py-2 text-sm text-n-slate-11"
      >
        {{ $t('AGENT_MGMT.STATUS_HISTORY.EMPTY') }}
      </p>
      <ul v-else class="divide-y divide-n-weak max-h-56 overflow-y-auto">
        <li
          v-for="change in history"
          :key="change.id"
          class="grid grid-cols-[auto_1fr_auto] gap-x-3 py-1.5 text-sm"
        >
          <span class="tabular-nums text-n-slate-11">
            {{ historyTime.format(new Date(change.created_at)) }}
          </span>
          <span class="text-n-slate-12">
            {{ $t(`AVAILABILITY.${change.from.toUpperCase()}`) }}
            →
            {{ $t(`AVAILABILITY.${change.to.toUpperCase()}`) }}
          </span>
          <span class="text-n-slate-11">{{ reasonText(change) }}</span>
        </li>
      </ul>
    </div>

    <div class="space-y-1">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('AGENT_MGMT.ADD.FORM.EMAIL.LABEL') }}
      </span>
      <p class="text-sm text-n-slate-11">{{ operator.email }}</p>
    </div>

    <div class="space-y-1">
      <span class="text-heading-3 text-n-slate-12">
        {{ $t('AGENT_MGMT.PASSWORD_LINK.LABEL') }}
      </span>
      <p class="text-xs text-n-slate-11">
        {{ $t('AGENT_MGMT.PASSWORD_LINK.HINT') }}
      </p>
      <Button
        type="button"
        sm
        faded
        slate
        :label="$t('AGENT_MGMT.PASSWORD_LINK.SUBMIT')"
        :disabled="sendingLink"
        @click="sendPasswordLink"
      />
    </div>

    <div class="flex justify-end gap-2 pt-2">
      <Button
        faded
        slate
        type="button"
        :label="$t('AGENT_MGMT.ADD.CANCEL')"
        @click="emit('close')"
      />
      <Button
        type="submit"
        :label="$t('AGENT_MGMT.EDIT.SUBMIT')"
        :disabled="uiFlags.isUpdating"
      />
    </div>
  </form>
</template>
